// Mobile-optimized admin/categories page
// Large touch-friendly category management

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  ChevronUp,
  ChevronDown,
  Edit2,
  Trash2,
  Sparkles,
  FolderOpen
} from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import useCategoryStore from '../../stores/categoryStore'
import { Modal, ConfirmModal } from '../common'
import CategoryForm from '../admin/CategoryForm'
import PromptEditor from '../admin/PromptEditor'

// Map icon names to Lucide components
const getIcon = (iconName) => {
  const iconMap = {
    'home': LucideIcons.Home,
    'trees': LucideIcons.Trees,
    'warehouse': LucideIcons.Warehouse,
    'utensils': LucideIcons.Utensils,
    'sofa': LucideIcons.Sofa,
    'bed-double': LucideIcons.BedDouble,
    'bed': LucideIcons.Bed,
    'bath': LucideIcons.Bath,
    'shower-head': LucideIcons.ShowerHead,
    'door-open': LucideIcons.DoorOpen,
    'flower-2': LucideIcons.Flower2,
    'car': LucideIcons.Car,
    'gauge': LucideIcons.Gauge,
    'folder': LucideIcons.Folder
  }
  return iconMap[iconName] || LucideIcons.Folder
}

export default function MobileAdminPage() {
  const navigate = useNavigate()

  const [editingCategory, setEditingCategory] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [showPromptEditor, setShowPromptEditor] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [expandedCategory, setExpandedCategory] = useState(null)

  const categories = useCategoryStore(state => state.categories)
  const loading = useCategoryStore(state => state.loading)
  const loadCategories = useCategoryStore(state => state.loadCategories)
  const deleteCategory = useCategoryStore(state => state.deleteCategory)
  const reorderCategories = useCategoryStore(state => state.reorderCategories)

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  const handleAddNew = () => {
    setEditingCategory(null)
    setShowForm(true)
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setShowForm(true)
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      await deleteCategory(deleteConfirm.id)
      setDeleteConfirm(null)
    } catch (error) {
      console.error('Failed to delete category:', error)
    }
  }

  const handleMoveUp = async (index) => {
    if (index === 0) return
    const newOrder = [...categories]
    const [moved] = newOrder.splice(index, 1)
    newOrder.splice(index - 1, 0, moved)
    await reorderCategories(newOrder.map(c => c.id))
  }

  const handleMoveDown = async (index) => {
    if (index === categories.length - 1) return
    const newOrder = [...categories]
    const [moved] = newOrder.splice(index, 1)
    newOrder.splice(index + 1, 0, moved)
    await reorderCategories(newOrder.map(c => c.id))
  }

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Header */}
      <div className="bg-amber-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-2 hover:bg-amber-500 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-lg font-semibold">Categories</h1>
            <p className="text-amber-200 text-sm">{categories.length} categories</p>
          </div>
        </div>
        <button
          onClick={handleAddNew}
          className="p-2 hover:bg-amber-500 rounded-lg"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Categories list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FolderOpen className="w-16 h-16 text-slate-300 mb-4" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">No categories yet</h2>
            <p className="text-slate-500 text-center mb-6">Add your first category to get started</p>
            <button
              onClick={handleAddNew}
              className="px-6 py-3 bg-amber-600 text-white rounded-xl font-semibold flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Category
            </button>
          </div>
        ) : (
          categories.map((category, index) => {
            const Icon = getIcon(category.icon)
            const isExpanded = expandedCategory === category.id

            return (
              <div
                key={category.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
              >
                {/* Category row */}
                <div className="p-4 flex items-center gap-3">
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0 || loading}
                      className="p-1.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 rounded"
                    >
                      <ChevronUp className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === categories.length - 1 || loading}
                      className="p-1.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 rounded"
                    >
                      <ChevronDown className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-amber-600" />
                  </div>

                  {/* Name */}
                  <button
                    className="flex-1 text-left"
                    onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  >
                    <h3 className="font-semibold text-slate-900">{category.name}</h3>
                    <p className="text-sm text-slate-500">
                      {category.aiPrompt ? 'AI prompt set' : 'No prompt'}
                    </p>
                  </button>

                  {/* Actions */}
                  <div className="flex gap-1">
                    <button
                      onClick={() => setShowPromptEditor(category)}
                      className="p-2 text-amber-500 hover:bg-amber-50 rounded-lg"
                    >
                      <Sparkles className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(category)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Expanded prompt */}
                {isExpanded && category.aiPrompt && (
                  <div className="px-4 pb-4">
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs font-medium text-slate-500 mb-1">AI Prompt:</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">
                        {category.aiPrompt}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Add/Edit Category Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        size="fullscreen"
      >
        <CategoryForm
          category={editingCategory}
          onClose={() => setShowForm(false)}
        />
      </Modal>

      {/* Prompt Editor Modal */}
      <Modal
        isOpen={!!showPromptEditor}
        onClose={() => setShowPromptEditor(null)}
        title={`AI Prompt - ${showPromptEditor?.name}`}
        size="fullscreen"
      >
        {showPromptEditor && (
          <PromptEditor
            category={showPromptEditor}
            onClose={() => setShowPromptEditor(null)}
          />
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        loading={loading}
      />
    </div>
  )
}
